import { Input } from "component/inputs/Input";
import React, { useCallback, useMemo, useState } from "react";
import { Field, Form } from "react-final-form";
import { ConnectedProps, connect } from "react-redux";
import {
  actions as detailTransactionActions,
  detailTransactionSelector,
} from "reducers/detailTransaction";
import { AppDispatch, RootState } from "store";
import { HistoryModalTransactionType } from "./types";
import "./HistoryModalTransaction.scss";
import {
  TRANSACTION_ACTIONS_TAG_LABEL,
  cancelStatus,
} from "utils/constants";
import { ISO8601toDDMMYYYHHMM } from "utils/date";
import { Button, StatusStepper } from "@pickit/pickit-components";
import "../i18n";
import { useTranslation } from "react-i18next";
import { TransactionConstants } from "constants/transactions";
import { TrxHistoryType, ProofType } from "../../../../sagas/types/detailTransactions";
import {
  PROOF_IMAGE_TYPES,
  getDeliveryCoords,
  getProofThumbnails,
  getReceiverInfo,
} from "./utils";
import { watermarkImageWithText } from "utils/waterMark";
import * as transactionsMiddleware from "middleware/transactions";

const FINAL_STATE_CANCEL = "CANCEL";
const FINAL_STATE_LOST = "LOST";

function ProofThumbnail({
  src,
  alt,
  onClick,
  testId,
  title,
}: {
  src: string;
  alt: string;
  onClick: () => void;
  testId: string;
  title: string;
}): JSX.Element {
  const [loaded, setLoaded] = useState(false);
  return (
    <button
      type="button"
      className="transaction-history-proof-thumb"
      title={title}
      data-testid={testId}
      onClick={onClick}
    >
      {!loaded && (
        <span className="transaction-history-proof-thumb-loader">
          <span className="transaction-history-proof-thumb-spinner" />
        </span>
      )}
      <img
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        style={loaded ? undefined : { display: "none" }}
      />
    </button>
  );
}

const HistoryModalTransaction: React.FC<
  HistoryModalTransactionType & ConnectorProps
> = ({
  detailTransaction,
  cancel,
  finish,
  next,
  getDetailTransaction,
  countryCode,
  getProof,
  testId,
  relationships = [],
}): JSX.Element => {
  const { t } = useTranslation();
  const { picker, client } = detailTransaction;
  const stepperTestId = "transaction-history";

  const relationshipTagToLabel = useCallback(
    (tag: string) => {
      const item = relationships.find(
        (r: { tag: string; value: string }) =>
          String(r.tag).toUpperCase() === String(tag).toUpperCase()
      );
      return item?.value ?? tag;
    },
    [relationships]
  );

  const SeePickerCallBack = useCallback(
    (pickerId: string) =>
      window.open(
        `/${countryCode}/pickers/detail/${pickerId}?tab=pickers`,
        "_blank"
      ),
    [countryCode]
  );

  const buttonStates = useMemo(
    () => ["assigned_picker", "state_delivered"] as const,
    []
  );

  const proofThumbnails = useMemo(
    () => getProofThumbnails(detailTransaction as unknown as Record<string, unknown>),
    [detailTransaction]
  );

  const proofImages = useMemo(
    () =>
      proofThumbnails.filter((p) =>
        (PROOF_IMAGE_TYPES as readonly string[]).includes(p.type)
      ),
    [proofThumbnails]
  );


  type ButtonStatesType = (typeof buttonStates)[number];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stepperButtons: {
    [key in ButtonStatesType]: (trx: TrxHistoryType) =>
      | {
          label: string;
          action: () => void;
          testId: string;
        }
      | undefined;
  } = useMemo(
    () => ({
      assigned_picker: (trx: TrxHistoryType) => {
        return {
          label: t("detailTransaction:seePicker"),
          action: () => SeePickerCallBack(trx.currentValue),
          testId: `${stepperTestId}-see-picker`,
        };
      },
      state_delivered: () => {
        return {
          label: "",
          action: () => {},
          testId: "",
        };
      },
    }),
    [SeePickerCallBack, t]
  );

  const deliveryCoords = useMemo(
    () => getDeliveryCoords(detailTransaction as unknown as Record<string, unknown>),
    [detailTransaction]
  );

  const openProofImageInNewTab = useCallback(
    (index: number) => {
      const proof = proofImages[index];
      const url = proof?.value;
      const transactionId = String(detailTransaction.transaction.id);
      const basePath = `/${countryCode}/transaction/delivery-evidence`;
      const search = new URLSearchParams({
        transactionId,
        singleIndex: String(index),
      });
      const openFallback = () =>
        window.open(`${basePath}?${search.toString()}`, "_blank", "noopener,noreferrer");

      if (!url) {
        openFallback();
        return;
      }

      const proofType: ProofType = proof.type.toUpperCase() === "SIGN" ? "sign" : "photo";

      const resolveCoords = (): Promise<{
        lat: string;
        lng: string;
        finishedByAdmin: boolean;
        timestamp?: Date;
      }> => {
        if (deliveryCoords.lat && deliveryCoords.lng) {
          return Promise.resolve({
            lat: deliveryCoords.lat,
            lng: deliveryCoords.lng,
            finishedByAdmin: deliveryCoords.finishedByAdmin,
            timestamp: deliveryCoords.timestamp,
          });
        }
        return transactionsMiddleware
          .getProofOfDelivery(transactionId)
          .then((res) => {
            if (res.status !== 200 || !res.data?.result) {
              return {
                lat: deliveryCoords.lat,
                lng: deliveryCoords.lng,
                finishedByAdmin: deliveryCoords.finishedByAdmin,
                timestamp: deliveryCoords.timestamp,
              };
            }
            const result = res.data.result as Record<string, unknown>;
            const rawProof = result?.proofOfDelivery as Record<string, unknown> | undefined;
            const hasLat = rawProof && typeof rawProof === "object" && !Array.isArray(rawProof) &&
              (rawProof.latitude != null || rawProof.lat != null);
            const hasLng = rawProof && typeof rawProof === "object" && !Array.isArray(rawProof) &&
              (rawProof.longitude != null || rawProof.lng != null);
            const isObject = hasLat && hasLng;
            const lat =
              isObject
                ? String(rawProof!.latitude ?? rawProof!.lat ?? "")
                : result?.latitude != null
                  ? String(result.latitude)
                  : result?.lat != null
                    ? String(result.lat)
                    : deliveryCoords.lat;
            const lng =
              isObject
                ? String(rawProof!.longitude ?? rawProof!.lng ?? "")
                : result?.longitude != null
                  ? String(result.longitude)
                  : result?.lng != null
                    ? String(result.lng)
                    : deliveryCoords.lng;
            const finishedByAdmin = Boolean(
              isObject && rawProof?.finishByAdmin != null
                ? rawProof.finishByAdmin
                : result?.finishByAdmin ?? deliveryCoords.finishedByAdmin
            );
            const rawTs = (isObject ? rawProof?.timestamp : result?.timestamp ?? (result as Record<string, unknown>)?.delivery_timestamp) as string | Date | undefined;
            const timestamp =
              rawTs instanceof Date && !Number.isNaN(rawTs.getTime())
                ? rawTs
                : typeof rawTs === "string"
                  ? (() => {
                      const d = new Date(rawTs);
                      return !Number.isNaN(d.getTime()) ? d : undefined;
                    })()
                  : deliveryCoords.timestamp;
            return { lat, lng, finishedByAdmin, timestamp };
          })
          .catch(() => ({
            lat: deliveryCoords.lat,
            lng: deliveryCoords.lng,
            finishedByAdmin: deliveryCoords.finishedByAdmin,
            timestamp: deliveryCoords.timestamp,
          }));
      };

      resolveCoords().then((coords) => {
        const hasCoords = Boolean(coords.lat && coords.lng);
        const watermarkText = [
          hasCoords
            ? `${t("detailTransaction:lat")} ${coords.lat}\n${t("detailTransaction:long")} ${coords.lng}`
            : "",
          t("detailTransaction:confidetialMenssage"),
        ]
          .filter(Boolean)
          .join("\n");

        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          try {
            const dataUrl = watermarkImageWithText(
              img,
              watermarkText,
              proofType,
              hasCoords,
              coords.timestamp
            );
            if (!dataUrl) {
              openFallback();
              return;
            }
            const fallbackUrl = `${window.location.origin}/${countryCode}/transaction/delivery-evidence?${search.toString()}`;
            const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${proof.type.toUpperCase() === "SIGN" ? "Firma" : "Foto"}</title></head><body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#000"><img src="${dataUrl.replace(/"/g, "&quot;")}" style="max-width:100%;max-height:100vh;object-fit:contain" alt="" /><script>document.querySelector("img").onerror=function(){window.location.href="${fallbackUrl.replace(/"/g, '\\"')}";};<${"/script>"}</body></html>`;
            const blob = new Blob([html], { type: "text/html;charset=utf-8" });
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, "_blank", "noopener,noreferrer");
            setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
          } catch {
            openFallback();
          }
        };
        img.onerror = openFallback;
        img.src = url;
      });
    },
    [
      countryCode,
      deliveryCoords.finishedByAdmin,
      deliveryCoords.lat,
      deliveryCoords.lng,
      deliveryCoords.timestamp,
      detailTransaction.transaction.id,
      proofImages,
      t,
    ]
  );

  const receiverInfo = useMemo(
    () =>
      getReceiverInfo(
        detailTransaction as unknown as Record<string, unknown>,
        relationshipTagToLabel
      ),
    [detailTransaction, relationshipTagToLabel]
  );

  const serialCodes = useMemo(() => {
    const codes = detailTransaction.transaction.deliveryValidation?.codes;
    if (Array.isArray(codes)) return codes.filter(Boolean);
    return codes ? [codes] : [];
  }, [detailTransaction.transaction.deliveryValidation?.codes]);

  const renderContent = useCallback(
    (transaction: TrxHistoryType) => {
      let content: React.ReactNode = undefined;

      if (
        transaction.rejectionReason &&
        transaction.rejectionReason.length > 0
      ) {
        content = `${t("detailTransaction:reason")}: ${
          transaction.rejectionReason[0].value
        }`;
      }

      if (transaction.reason === "state_delivered") {
        const receiverParts: string[] = [];
        if (receiverInfo) {
          const hasName = receiverInfo.name !== "-";
          const hasLastName = receiverInfo.lastName !== "-";
          if (hasName && hasLastName) {
            receiverParts.push(
              `${t("detailTransaction:receiverNameAndLastName")} ${receiverInfo.fullName}`
            );
          } else if (hasName) {
            receiverParts.push(
              `${t("detailTransaction:receiverName")} ${receiverInfo.name}`
            );
          } else if (hasLastName) {
            receiverParts.push(
              `${t("detailTransaction:receiverLastName")} ${receiverInfo.lastName}`
            );
          }
          if (receiverInfo.dni !== "-") {
            receiverParts.push(
              `${t("detailTransaction:receiverDni")} ${receiverInfo.dni}`
            );
          }
          if (receiverInfo.relationship !== "-") {
            receiverParts.push(
              `${t("detailTransaction:receiverRelationship")} ${receiverInfo.relationship}`
            );
          }
        }
        const receiverBlock =
          receiverParts.length > 0 ? (
            <div className="transaction-history-receiver-info">
              {receiverParts.join(". ")}
            </div>
          ) : null;
        let deliveryContent: React.ReactNode;
        if (deliveryCoords.lat && deliveryCoords.lng) {
          const mapsUrl = `https://www.google.com/maps?q=${encodeURIComponent(
            `${deliveryCoords.lat},${deliveryCoords.lng}`
          )}`;
          const coordsText = `${deliveryCoords.lat}, ${deliveryCoords.lng}`;
          deliveryContent = (
            <>
              <Button
                type="button"
                hierarchy="link"
                size="small"
                label={t("detailTransaction:openInGoogleMaps")}
                onClick={() => window.open(mapsUrl, "_blank", "noopener,noreferrer")}
                testId={`${stepperTestId}-open-google-maps`}
              />
              <span className="transaction-history-coords-text">{coordsText}</span>
              {deliveryCoords.finishedByAdmin && (
                <>
                  {" · "}
                  <span className="transaction-history-finish-by-admin">
                    {t("detailTransaction:finishByAdmin")}
                  </span>
                </>
              )}
            </>
          );
        } else if (deliveryCoords.finishedByAdmin) {
          deliveryContent = t("detailTransaction:finishByAdmin");
        } else {
          deliveryContent = t("detailTransaction:deliveryCoordinates", {
            coordinates: "-",
            reason: "",
          });
        }
        content = (
          <div className="transaction-history-delivered-content">
            {receiverBlock}
            <div className="transaction-history-delivered-coords">
              {deliveryContent}
            </div>
            {/* {seeEvidenceButton} */}
          </div>
        );
      }

      if (transaction.tripCode && transaction.currentValue === "IN_PICK_UP") {
        content = `${t("detailTransaction:tripCode")}: ${transaction.tripCode}`;
      }

      return content;
    },
    [deliveryCoords, receiverInfo, stepperTestId, t]
  );

  const cancelStepTransactionHistory: TrxHistoryType[] = [];
  detailTransaction.transactionHistory.map((trx) => {
    if (cancelStatus.includes(trx.reason as string)) {
      if (
        trx.currentValue !== FINAL_STATE_CANCEL &&
        trx.currentValue !== FINAL_STATE_LOST
      )
        cancelStepTransactionHistory.push(trx);
    } else if (
      cancelStepTransactionHistory.some((newTrx) => trx.id < newTrx.id) &&
      trx.reason !== "state_initial"
    ) {
      cancelStepTransactionHistory.push(trx);
    }
    return cancelStepTransactionHistory;
  });

  const steps = useMemo(
    () =>
      detailTransaction.transactionHistory.map((trx, i) => {
        return {
          status:
            cancelStepTransactionHistory.includes(trx) ||
            trx.currentValue === FINAL_STATE_CANCEL ||
            trx.currentValue === FINAL_STATE_LOST
              ? ("cancel" as any)
              : ("success" as any),
          title: t(TRANSACTION_ACTIONS_TAG_LABEL[trx.reason as string]),
          subtitle: ISO8601toDDMMYYYHHMM(trx.createdAt),
          content: renderContent(trx),

          button:
            trx.reason === "state_delivered"
              ? undefined
              : buttonStates.includes(trx.reason as ButtonStatesType)
                ? stepperButtons[trx.reason as ButtonStatesType](trx)
                : undefined,
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      buttonStates,
      detailTransaction.transactionHistory,
      renderContent,
      stepperButtons,
      t,
    ]
  );

  return (
    <div className="moda-content-container">
      <div className="modal-transaction-scroll">
        <div className="modal-transaction-container-scroll">
          <Form
            onSubmit={() => {}}
            initialValues={{
              areaCode: picker.phone ? picker.phone.areaNumber : "-",
              pickerId: picker.id ? picker.id : "-",
              name: picker.name ? `${picker.name} ${picker.surname}` : "-",
              phone: picker.phone ? picker.phone.number : "-",
              deliveryAddress: detailTransaction.destination.formattedAddress,
              pickupAddress: detailTransaction.origin.formattedAddress,
              retailer:
                detailTransaction.seller &&
                detailTransaction.seller.name !== null
                  ? detailTransaction.seller.name
                  : "-",
              sku: detailTransaction.products.length > 1
                ? detailTransaction.products
                    .map((product) => product.sku?.trim() || "-")
                    .join(", ")
                : detailTransaction.products[0]?.sku?.trim() || "-",
              serialNumber: Array.isArray(
                detailTransaction.transaction.deliveryValidation?.codes
              )
                ? detailTransaction.transaction.deliveryValidation?.codes
                : detailTransaction.transaction.deliveryValidation?.codes
                  ? [detailTransaction.transaction.deliveryValidation.codes]
                  : [],
              region: detailTransaction.region?.name
                ? detailTransaction.region?.name
                : "-",
              trip: detailTransaction.trip?.id
                ? detailTransaction.trip?.id
                : "-",
              receiverName: `${client.name ? client.name : ""} ${client.lastName ? client.lastName : ""}`.trim() || "-",
              receiverPhone: client.phone ? client.phone : "-",
            }}
          >
            {({ handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                <div className="container-history-row">
                  <div className="container-history-col">
                    <div className="container-history-row">
                      <div className="container-history-col-4">
                        <Field
                          type="text"
                          name="pickerId"
                          label={t("detailTransaction:pickerId")}
                          component={Input}
                          className="Admin-Pickers-input input-modal-transaction"
                          disabled
                          testId={`${testId}-picker-id`}
                          id={`${testId}-picker-id`}
                        />
                      </div>
                      <div className="container-history-col-4">
                        <Field
                          type="text"
                          name="name"
                          label={t("detailTransaction:pickerFullName")}
                          component={Input}
                          className="Admin-Pickers-input input-modal-transaction"
                          disabled
                          testId={`${testId}-picker-full-name`}
                          id={`${testId}-picker-full-name`}
                        />
                      </div>

                      <div className="container-history-col-2">
                        <Field
                          type="text"
                          name="areaCode"
                          label={t("detailTransaction:areaCode")}
                          component={Input}
                          className="Admin-Pickers-input input-modal-transaction input-modal-transaction"
                          disabled
                          testId={`${testId}-area-code`}
                          id={`${testId}-area-code`}
                        />
                      </div>
                      <div className="container-history-col-2">
                        <Field
                          type="text"
                          name="phone"
                          label={t("detailTransaction:phone")}
                          component={Input}
                          className="Admin-Pickers-input input-modal-transaction"
                          disabled
                          testId={`${testId}-phone`}
                          id={`${testId}-phone`}
                        />
                      </div>

                      <div className="container-history-col-4">
                        <Field
                          type="text"
                          name="pickupAddress"
                          label={t("detailTransaction:pickupAddress")}
                          component={Input}
                          className="Admin-Pickers-input input-modal-transaction"
                          disabled
                          testId={`${testId}-pickup-address`}
                          id={`${testId}-pickup-address`}
                        />
                      </div>
                      <div className="container-history-col-4">
                        <Field
                          type="text"
                          name="deliveryAddress"
                          label={t("detailTransaction:deliveryAddress")}
                          component={Input}
                          className="Admin-Pickers-input input-modal-transaction"
                          disabled
                          testId={`${testId}-delivery-address`}
                          id={`${testId}-delivery-address`}
                        />
                      </div>

                      <div className="container-history-col-4">
                        <Field
                          type="text"
                          name="region"
                          label={t("detailTransaction:region")}
                          component={Input}
                          className="Admin-Pickers-input input-modal-transaction"
                          disabled
                          testId={`${testId}-region`}
                          id={`${testId}-region`}
                        />
                      </div>
                      <div className="container-history-col-4">
                        <Field
                          type="text"
                          name="trip"
                          label={t("detailTransaction:tripCodeLabel")}
                          component={Input}
                          className="Admin-Pickers-input input-modal-transaction"
                          disabled
                          testId={`${testId}-trip-code`}
                          id={`${testId}-trip-code`}
                        />
                      </div>
                      <div className="container-history-col-4">
                        <Field
                          type="text"
                          name="retailer"
                          label={t("detailTransaction:retailer")}
                          component={Input}
                          className="Admin-Pickers-input input-modal-transaction"
                          disabled
                          testId={`${testId}-retailer`}
                          id={`${testId}-retailer`}
                        />
                      </div>
                      <div className="container-history-col-4">
                        <Field
                          type="text"
                          name="sku"
                          label={t("detailTransaction:sku")}
                          component={Input}
                          className="Admin-Pickers-input input-modal-transaction"
                          disabled
                          testId={`${testId}-sku`}
                          id={`${testId}-sku`}
                        />
                      </div>
                      {detailTransaction.transaction.deliveryValidation?.type ===
                        "SERIAL_NUMBER" &&
                        serialCodes.length > 0 && (
                          <div className="container-history-col transaction-history-serial-list">
                            <p className="transaction-history-serial-list-title">
                            {serialCodes.length > 1
                                ? t("detailTransaction:serialNumbersLabel")
                                : t("detailTransaction:serialNumberLabel")}
                            </p>
                            {serialCodes.map((code, index) => (
                              <p
                                key={`serial-number-${index}`}
                                className="transaction-history-serial-list-item"
                                data-testid={`${testId}-serial-number-${index}`}
                                id={`${testId}-serial-number-${index}`}
                              >
                                {code}
                              </p>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                </div>

                <h3
                  className="modal-history-h3"
                  id="modal-transaction-history-Final"
                  data-testId={`${testId}-consumidor-final-title`}
                >
                  {t("detailTransaction:finalConsumer")}
                </h3>
                <hr
                  className="modal-transaction-separate"
                  id="modal-transaction-hr-title"
                />
                <div className="container-history-row">
                  <div className="container-history-col">
                    <div className="container-history-row">
                      <div className="container-history-col-4">
                        <Field
                          type="text"
                          name="receiverName"
                          label={t("detailTransaction:pickerFullName")}
                          component={Input}
                          className="Admin-Pickers-input input-modal-transaction"
                          disabled
                          testId={`${testId}-receiver-name`}
                          id={`${testId}-receiver-name`}
                        />
                      </div>
                      <div className="container-history-col-4">
                        <Field
                          type="text"
                          name="receiverPhone"
                          label={t("detailTransaction:phone")}
                          component={Input}
                          className="Admin-Pickers-input input-modal-transaction"
                          disabled
                          testId={`${testId}-receiver-phone`}
                          id={`${testId}-receiver-phone`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </Form>
          <div className="transaction-history-container">
            {proofImages.length > 0 && (
              <div className="transaction-history-proof-thumbnails">
                {proofImages.map((proof, index) =>
                  proof ? (
                    <ProofThumbnail
                      key={`${proof.type}-${index}`}
                      src={proof.value}
                      alt={proof.type}
                      onClick={() => openProofImageInNewTab(index)}
                      testId={`${testId}-proof-thumb-${proof.type.toLowerCase()}-${index}`}
                      title={t("detailTransaction:seeDeliveryEvidence")}
                    />
                  ) : (
                    <span
                      key={`loader-${index}`}
                      className="transaction-history-proof-thumb transaction-history-proof-thumb-loader"
                    >
                      <span className="transaction-history-proof-thumb-spinner" />
                    </span>
                  )
                )}
              </div>
            )}
            <StatusStepper status={steps as any} width="100%" testId={stepperTestId} />
          </div>
          <div className="transaction-footer-transparent" />
        </div>
      </div>
      <div className="button-container">
        <button
          disabled={
            !TransactionConstants.isCancelEnabled(
              detailTransaction.transaction.state.id
            )
          }
          onClick={() => {
            next(cancel);
          }}
          className="button-modal-transaction"
          data-testId={`${testId}-cancel-button`}
        >
          {t("global:cancel")}
        </button>
        <button
          disabled={
            !TransactionConstants.isFinishEnabled(
              detailTransaction.transaction.state.id
            )
          }
          onClick={() => {
            next(finish);
          }}
          className="button-modal-transaction"
          data-testId={`${testId}-finish-button`}
        >
          {t("global:finish")}
        </button>
        <div className="refresh-button-container">
          <Button
            label={t("global:update")}
            type="button"
            hierarchy="minimal"
            iconName="refresh"
            onClick={() =>
              getDetailTransaction(`${detailTransaction.transaction.id}`)
            }
            testId={`${testId}-update-button`}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  detailTransaction: detailTransactionSelector(state).detailTransaction,
  relationships: detailTransactionSelector(state).relationships ?? [],
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getDetailTransaction: (id: string) => {
    dispatch(detailTransactionActions.getDetailTransactionRequest(id));
  },
  getProof: (id: string, waterMarkText: string, type: ProofType) => {
    dispatch(
      detailTransactionActions.getProofRequest({ id, waterMarkText, type })
    );
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(HistoryModalTransaction);
